import { FormApi } from "final-form";
import { Form, useForm, useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useSelector } from "src/store";

import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { Provisioning } from "@dashboard/provisioning/types";
import { Row, SectionDescription } from "@dashboard/shared/styles";
import { Typography, useTheme } from "@mui/material";
import { DangerOutlinedButton, PrimaryOutlinedButton } from "@shared/CustomButton";
import { TextField } from "@shared/form/TextField";
import {
    MinimalTable,
    MinimalTableBody,
    MinimalTableCell,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
    MinimalTableRow,
} from "@shared/table/MinimalTableComponents";

import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
    useCollapsiblePersistence,
} from "../../shared/components/CollapsibleSection";

const initialValues = { disableComponent: undefined };

type FormValues = { disableComponent: string };

export const DisabledComponents = () => {
    const theme = useTheme();
    const { t } = useTranslation(["pages", "common"]);
    const { value, onChange } = useCollapsiblePersistence("provisioningPage.collapsedSections", "DisabledComponents");
    const parentForm = useForm();
    const provisionings = useSelector(selectProvisionings, shallowEqual);

    const { values: parentFormValues } = useFormState<Partial<Provisioning>>();
    const components = parentFormValues?.settings?.disabledComponents || [];

    const onSubmit = (values: FormValues, form: FormApi<FormValues>) => {
        if (components.length) {
            parentForm.change("settings.disabledComponents", [...components, values.disableComponent]);
        }
        if (!components.length) {
            parentForm.change("settings.disabledComponents", [values.disableComponent]);
        }
        form.restart(initialValues);
    };
    const validate = (values: FormValues) => {
        if (components.includes(values.disableComponent)) {
            return { disableComponent: t("pages:provisioningPage.input.disabledComponent.validation.alreadyAdded") };
        }
        if (!values.disableComponent) {
            return { disableComponent: t("pages:provisioningPage.input.disabledComponent.validation.empty") };
        }
    };
    const handleDelete = (index: number) => {
        parentForm.change(
            "settings.disabledComponents",
            components.filter((_, i) => i !== index),
        );
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({ form }) => (
                <CollapsibleProvider initialValue={value} onChange={onChange}>
                    <CollapsibleSectionSummary>
                        <CollapsibleSectionTitle>
                            {t("pages:provisioningPage.section.disabledComponents.title")}
                        </CollapsibleSectionTitle>
                    </CollapsibleSectionSummary>
                    <CollapsibleSectionDetails>
                        <SectionDescription>
                            {t("pages:provisioningPage.section.disabledComponents.description")}
                        </SectionDescription>
                        <MinimalTableContainer>
                            <MinimalTable>
                                <MinimalTableHeader>
                                    <MinimalTableRow>
                                        <MinimalTableHeaderCell>
                                            <Typography fontWeight="bold">
                                                {t("pages:provisioningPage.table.header.component")}
                                            </Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="right"></MinimalTableHeaderCell>
                                    </MinimalTableRow>
                                </MinimalTableHeader>
                                <MinimalTableBody>
                                    {components.length ? (
                                        components.map((component, index) => (
                                            <MinimalTableRow key={component}>
                                                <MinimalTableCell component="th" scope="row">
                                                    <Typography>{component}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="right">
                                                    <DangerOutlinedButton
                                                        name="disabled-component-delete"
                                                        onClick={() => handleDelete(index)}
                                                        tooltipProps={{ title: t("common:button.delete") }}
                                                    >
                                                        <i className="fa-solid fa-trash" />
                                                    </DangerOutlinedButton>
                                                </MinimalTableCell>
                                            </MinimalTableRow>
                                        ))
                                    ) : (
                                        <MinimalTableRow>
                                            <MinimalTableCell align="center" colSpan={2}>
                                                <Typography>{t("pages:provisioningPage.table.noDisabledComponents")}</Typography>
                                            </MinimalTableCell>
                                        </MinimalTableRow>
                                    )}
                                </MinimalTableBody>
                            </MinimalTable>
                        </MinimalTableContainer>
                        <Row style={{ marginTop: theme.spacing(4) }}>
                            <TextField
                                name="disableComponent"
                                label={t("pages:provisioningPage.input.disabledComponent.caption")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                            <PrimaryOutlinedButton
                                onClick={form.submit}
                                disabled={!provisionings.length}
                                name="disabled-component-add"
                            >
                                {t("common:button.add")}
                            </PrimaryOutlinedButton>
                        </Row>
                    </CollapsibleSectionDetails>
                </CollapsibleProvider>
            )}
        />
    );
};
